import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  Flex,
  Text,
} from '@chakra-ui/react';
import { FormikProps } from 'formik';
import _ from 'lodash';
import Prism from 'prismjs';
import React, { useEffect, useState } from 'react';

import { getConcentrationLimitConfigCode } from '@finley-technologies/pointer-methods';

import { CreditAgreement, WipConfig } from '../../types';
import { ConcentrationLimitConfigDuelEditor } from '../edit/ConcentrationLimitConfigDuelEditor';
import { colors } from 'packages/common-frontend/colors';
import { Icon as FinleyIcon } from 'packages/common-frontend/components/icons';
import './concentrationLimitConfig.scss';

import 'prismjs/themes/prism-okaidia.min.css';
import 'prismjs/components/prism-typescript.min';
import 'prismjs/components/prism-javascript.min';

Prism.manual = true;

// Fragments
type TSCodeHighlightProps = { children: string };
const TSCodeHighlight = ({ children }: TSCodeHighlightProps) => {
  useEffect(() => {
    Prism.highlightAll();
  }, [children]);
  return (
    <Box maxWidth="100%">
      <pre word-break="break-word !important">
        <code
          className="language-typescript"
          word-break="break-word !important"
        >
          {children}
        </code>
      </pre>
    </Box>
  );
};

// Exports
type ConcentrationLimitConfigConditionalPropertiesProps = {
  localFormProps: FormikProps<any>;
  validationErrors: Array<string>;
  wipConfig: WipConfig | undefined;
  concentrationLimitKey: string;
  configPrefix: string;
  concentrationLimitConfigPrefix: string;
  fieldValuePrefix: string;
  setEditorErrors: React.Dispatch<React.SetStateAction<string[]>>;
  availableFields: CreditAgreement.Config.ConcentrationLimit.Calculation.AvailableField[];
};
export const ConcentrationLimitConfigConditionalProperties = ({
  availableFields,
  localFormProps,
  validationErrors,
  wipConfig,
  concentrationLimitKey,
  configPrefix,
  concentrationLimitConfigPrefix,
  fieldValuePrefix,
  setEditorErrors,
}: ConcentrationLimitConfigConditionalPropertiesProps) => {
  const [
    isPointerMethodCodeOpen,
    setIsPointerMethodCodeOpen,
  ] = useState<boolean>(false);

  const isLenderWipMode = wipConfig?.operationMode === 'lender';

  return (
    <>
      {!_.isEmpty(validationErrors) && (
        <Alert status="error" mb="20px" mt="20px">
          <AlertIcon />
          <Box>
            <AlertTitle>Errors</AlertTitle>
            <AlertDescription>
              {_.map(validationErrors, (error, index) => (
                <Text key={index}>{error}</Text>
              ))}
            </AlertDescription>
          </Box>
        </Alert>
      )}
      <Box>
        {wipConfig?.isMutateAllowed(concentrationLimitKey) && (
          <>
            {!isLenderWipMode &&
              localFormProps.values[configPrefix][
                concentrationLimitConfigPrefix
              ] &&
              localFormProps.values[configPrefix][
                concentrationLimitConfigPrefix
              ].calculationConfig &&
              localFormProps.values[configPrefix][
                concentrationLimitConfigPrefix
              ].calculationConfig.type === 'pointer' && (
                <Button
                  aria-label={
                    isPointerMethodCodeOpen
                      ? 'Close pointer method preview'
                      : 'Open pointer method preview'
                  }
                  onClick={() =>
                    setIsPointerMethodCodeOpen(!isPointerMethodCodeOpen)
                  }
                  colorScheme="aurora"
                  backgroundColor="white"
                  variant="outline"
                  borderWidth="2px"
                  mb="24px"
                >
                  <Flex alignItems="center">
                    <FinleyIcon icon="code" color={colors.aurora[400]} />
                    <Text ml="16px" color={colors.aurora[400]}>
                      {isPointerMethodCodeOpen
                        ? 'Close pointer method preview'
                        : 'Open pointer method preview'}
                    </Text>
                  </Flex>
                </Button>
              )}
            <ConcentrationLimitConfigDuelEditor
              value={JSON.stringify(
                localFormProps.values[configPrefix][
                  concentrationLimitConfigPrefix
                ],
                null,
                4,
              )}
              onChange={(value) => {
                const initialConfigObj =
                  localFormProps.values[configPrefix][
                    concentrationLimitConfigPrefix
                  ];
                try {
                  if (value) {
                    const newConfigObj = JSON.parse(value);
                    localFormProps.setFieldValue(
                      fieldValuePrefix,
                      newConfigObj,
                    );
                  }
                } catch (err) {
                  // if json parse fails, most likely due to intellisense or syntax errors in editor, config is equal to initial config
                  localFormProps.setFieldValue(
                    fieldValuePrefix,
                    initialConfigObj,
                  );
                }
              }}
              onValidate={(markers) => {
                const markerErrors = _.map(markers, (marker) => marker.message);
                setEditorErrors(markerErrors);
              }}
              operationMode={wipConfig.operationMode}
              availableFields={availableFields}
            />
            {!isLenderWipMode && isPointerMethodCodeOpen && (
              <>
                <Text fontWeight="bold" mt="35px" mb="10px">
                  Pointer Method
                </Text>
                <TSCodeHighlight>
                  {getConcentrationLimitConfigCode(
                    localFormProps.values[configPrefix][
                      concentrationLimitConfigPrefix
                    ],
                  )}
                </TSCodeHighlight>
              </>
            )}
          </>
        )}
      </Box>
    </>
  );
};
